import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { AuthService } from "../services/auth.service";
import {
  checkPassword,
  isPasswordValid,
  PASSWORD_MIN_LENGTH,
  PasswordRules,
  SPECIAL_CHARACTERS,
} from "../shared/utils";

// The set is not guessable: Firebase does not count + - or =, and the screens refuse
// / and " on top of that, so it is listed in full rather than sampled.
const SPECIAL_CHARACTER_LABEL = `At least one of these special characters: ${Array.from(
  SPECIAL_CHARACTERS,
).join(" ")}`;

const SUPPORTED_MODES = ["resetPassword", "verifyEmail", "recoverEmail"];

@Component({
  selector: "app-auth-action",
  templateUrl: "./auth-action.component.html",
  styleUrls: ["./auth-action.component.css"],
})
export class AuthActionComponent implements OnInit {
  mode: string | null = null;
  oobCode: string | null = null;

  model = { password: "", confirm: "" };

  readonly minLength = PASSWORD_MIN_LENGTH;
  rules: PasswordRules = checkPassword("");
  passwordValid = false;
  readonly checklist: { rule: keyof PasswordRules; label: string }[] = [
    {
      rule: "isLongEnough",
      label: `At least ${PASSWORD_MIN_LENGTH} characters`,
    },
    { rule: "hasLowercase", label: "At least one lowercase letter (a-z)" },
    { rule: "hasUppercase", label: "At least one uppercase letter (A-Z)" },
    { rule: "hasNumber", label: "At least one number (0-9)" },
    { rule: "hasSpecialChar", label: SPECIAL_CHARACTER_LABEL },
    {
      rule: "noForbiddenChar",
      label: 'No spaces, forward slashes (/) or double quote marks (")',
    },
  ];

  submitting = false;
  success = false;
  successMessage = "";
  unsupported = false;
  errorMessage: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private authService: AuthService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    // Deliberately read params ONLY here — do not touch the oobCode on load.
    // Firebase action codes are single-use; calling confirmPasswordReset /
    // applyActionCode during ngOnInit lets email and link scanners that
    // pre-fetch the URL consume the code before the user acts, which is what
    // produced the "expired or already used" error. All code usage is deferred
    // to an explicit user action (onSubmit / onApply).
    const params = this.route.snapshot.queryParamMap;
    this.mode = params.get("mode");
    this.oobCode = params.get("oobCode");

    if (!this.mode || SUPPORTED_MODES.indexOf(this.mode) === -1) {
      this.unsupported = true;
    }
  }

  onPasswordChanged(password: string) {
    this.model.password = password;
    this.rules = checkPassword(password);
    this.passwordValid = isPasswordValid(this.rules);
  }

  // resetPassword — consumes the code on submit of the new password.
  onSubmit() {
    this.errorMessage = null;

    if (!this.oobCode) {
      this.errorMessage =
        "Invalid or missing reset code. Please request a new password reset email.";
      return;
    }
    if (this.model.password !== this.model.confirm) {
      this.errorMessage = "Passwords do not match.";
      return;
    }

    this.submitting = true;
    this.authService
      .ConfirmPasswordReset(this.oobCode, this.model.password)
      .then(() => {
        this.successMessage =
          "Your password has been updated. You can now sign in with your new password.";
        this.success = true;
        this.submitting = false;
      })
      .catch((error) => {
        this.submitting = false;
        this.errorMessage = this.mapError(error);
      });
  }

  // verifyEmail / recoverEmail — consumes the code on the user's button click.
  onApply() {
    this.errorMessage = null;

    if (!this.oobCode) {
      this.errorMessage =
        "This link is invalid or missing required information. Please request a new email.";
      return;
    }

    this.submitting = true;
    this.authService
      .ApplyActionCode(this.oobCode)
      .then(() => {
        this.successMessage =
          this.mode === "recoverEmail"
            ? "Your email address has been restored. If you didn't request this change, we recommend resetting your password."
            : "Your email address has been verified. You can now sign in.";
        this.success = true;
        this.submitting = false;
      })
      .catch((error) => {
        this.submitting = false;
        this.errorMessage = this.mapError(error);
      });
  }

  goToLogin() {
    this.router.navigate(["login"]);
  }

  private mapError(error: any): string {
    const code = error && error.code ? error.code : "";
    switch (code) {
      case "auth/expired-action-code":
        return "This link has expired. Please request a new email.";
      case "auth/invalid-action-code":
        return "This link is invalid or has already been used. Please request a new email.";
      case "auth/user-disabled":
        return "This account has been disabled. Please contact your administrator.";
      case "auth/user-not-found":
        return "No account was found for this link.";
      case "auth/weak-password":
        return "Password is too weak. Please choose a stronger password.";
      case "auth/too-many-requests":
        return "Too many attempts. Please wait a few minutes and try again.";
      default:
        // Firebase 8 has no code of its own for a password policy rejection: it
        // reports one as an internal error whose message carries the Identity
        // Toolkit reason, so this is matched on the text.
        if (
          error &&
          typeof error.message === "string" &&
          error.message.indexOf("PASSWORD_DOES_NOT_MEET_REQUIREMENTS") !== -1
        ) {
          return "That password does not meet the requirements listed above. Please choose a stronger one.";
        }
        // The raw message can be a JSON blob, which is no use to whoever reads it.
        console.error("Auth action failed", this.mode, error);
        return "Something went wrong. Please try again.";
    }
  }
}
